<template>
    <div>
        <header
            class="fixed top-0 left-0 right-0 z-[1000] w-full px-2 text-sm border-b custom_border md:px-6 lg:px-8 2xl:px-10"
        >
            <div class="relative">
                <!-- corners  -->
                <div class="absolute inset-0 z-[2] pointer-events-none" aria-hidden="true">
                    <div class="relative size-full">
                        <span class="absolute top-0 left-0 border-t-[1.5px] border-l-[1.5px] border-primary size-[10px]"></span>
                        <span class="absolute bottom-0 left-0 border-b-[1.5px] border-l-[1.5px] border-primary size-[10px]"></span>
                        <span class="absolute top-0 right-0 border-t-[1.5px] border-r-[1.5px] border-primary size-[10px]"></span>
                        <span class="absolute bottom-0 right-0 border-b-[1.5px] border-r-[1.5px] border-primary size-[10px]"></span>
                    </div>
                </div>

                <div class="relative w-full text-white dark_glass">
                    <div
                        class="header_bar grid items-center w-full grid-cols-[1fr_auto] lg:grid-cols-[1fr_auto_1fr] gap-4 px-4 lg:px-8"
                        :class="isScrolled ? 'h-16' : 'h-16 lg:h-20'"
                    >
                        <!-- logo -->
                        <NuxtLinkLocale to="/" class="justify-self-start" :aria-label="$t('nav.home')">
                            <img class="w-32 h-12" :src="logoTypoWhite" alt="Echopus logo" width="128" height="48" fetchpriority="high" decoding="async">
                        </NuxtLinkLocale>

                        <!-- primary nav (desktop) -->
                        <nav class="items-center hidden gap-1 lg:flex xl:gap-3" :aria-label="$t('nav.main')">
                            <NuxtLinkLocale
                                v-for="item in navItems"
                                :key="item.route"
                                :to="item.route"
                                active-class="is-active"
                                class="nav_link"
                            >
                                <span class="nav_bracket" aria-hidden="true">[</span>
                                <span>{{ item.title }}</span>
                                <span class="nav_bracket" aria-hidden="true">]</span>
                            </NuxtLinkLocale>
                        </nav>

                        <!-- actions -->
                        <div class="flex items-center gap-3 justify-self-end">
                            <div class="hidden lg:flex">
                                <LocaleToggle :locales="availableLocales" :current="locale" @change="changeLocale" />
                            </div>

                            <NuxtLinkLocale to="/contact/" class="hidden header_cta lg:inline-flex">
                                <span>{{ $t('nav.letsTalk') }}</span>
                                <svg class="size-4 rtl:-scale-x-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                            </NuxtLinkLocale>

                            <!-- menu button (mobile / tablet) -->
                            <button
                                type="button"
                                class="grid border rounded-full lg:hidden size-11 place-content-center border-primary-light text-primary"
                                :aria-expanded="isMenuOpen"
                                aria-controls="mobile-menu"
                                :aria-label="isMenuOpen ? $t('nav.closeMenu') : $t('nav.openMenu')"
                                @click="toggleMenu"
                            >
                                <AppIcon :name="isMenuOpen ? 'multiplication-sign' : 'menu-09'" class="text-[1.6rem] leading-none" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!-- mobile menu  -->
        <div
            id="mobile-menu"
            class="fixed left-0 w-full px-2 transition-all duration-300 md:px-6 lg:hidden top-16 z-[999] h-[calc(100dvh-4rem)]"
            :class="isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'"
            :aria-hidden="!isMenuOpen"
        >
            <div class="flex flex-col h-full px-6 pt-8 pb-8 overflow-y-auto md:px-12 dark_glass">
                <nav :aria-label="$t('nav.main')">
                    <ul class="flex flex-col">
                        <li
                            v-for="(item, i) in mobileItems"
                            :key="item.route"
                            class="mobile_item border-b border-dashed border-primary-light"
                            :class="{ 'is-open': isMenuOpen }"
                            :style="{ transitionDelay: isMenuOpen ? `${60 + i * 45}ms` : '0ms' }"
                        >
                            <NuxtLinkLocale
                                :to="item.route"
                                :exact-active-class="item.route === '/' ? 'is-active' : undefined"
                                :active-class="item.route === '/' ? undefined : 'is-active'"
                                class="flex items-center justify-between py-4 mobile_link"
                                :tabindex="isMenuOpen ? 0 : -1"
                                @click="closeMenu"
                            >
                                <span class="flex items-baseline gap-4">
                                    <span class="text-xs font-gt-light text-primary-light">{{ String(i + 1).padStart(2, '0') }}</span>
                                    <span class="text-3xl text-white font-pp">{{ item.title }}</span>
                                </span>
                                <img :src="trArrowSmall" alt="" aria-hidden="true" width="24" height="24" class="rtl:-scale-x-100">
                            </NuxtLinkLocale>
                        </li>
                    </ul>
                </nav>

                <div class="flex flex-col gap-6 pt-8 mt-auto">
                    <NuxtLinkLocale to="/contact/" class="flex justify-center w-full header_cta h-14" :tabindex="isMenuOpen ? 0 : -1" @click="closeMenu">
                        <span>{{ $t('nav.letsTalk') }}</span>
                        <svg class="size-4 rtl:-scale-x-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                    </NuxtLinkLocale>

                    <div class="flex items-center justify-between">
                        <LocaleToggle :locales="availableLocales" :current="locale" long @change="changeLocale" />

                        <ul class="flex items-center gap-2">
                            <li v-for="social in socials" :key="social.name">
                                <a
                                    :href="social.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :aria-label="social.name"
                                    :tabindex="isMenuOpen ? 0 : -1"
                                    class="grid transition border rounded-full size-10 place-content-center border-primary-light text-primary hover:border-primary"
                                >
                                    <AppIcon :name="social.icon" class="text-lg" />
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, h } from 'vue'
import logoTypoWhite from '~/assets/icons/logo-typo-white.svg'
import trArrowSmall from '~/assets/icons/tr-arrow-small.svg'

const { t, locale, locales, setLocale } = useI18n()
const route = useRoute()
const { $lenis } = useNuxtApp()

// URLs carry no locale prefix; setLocale switches in place and stores the choice in the i18n cookie.
const changeLocale = (code) => {
    if (code !== locale.value) setLocale(code)
}

const availableLocales = computed(() =>
    locales.value.filter((loc) => typeof loc === 'object')
)

// Desktop keeps the bar light: Home lives on the logo, Contact is the CTA button.
const navItems = computed(() => [
    { title: t('nav.about'), route: '/about/' },
    { title: t('nav.story'), route: '/our-story/' },
    { title: t('nav.projects'), route: '/projects/' },
    { title: t('nav.products'), route: '/products/' },
    { title: t('nav.careers'), route: '/careers/' },
])

const mobileItems = computed(() => [
    { title: t('nav.home'), route: '/' },
    ...navItems.value,
    { title: t('nav.contact'), route: '/contact/' },
])

const socials = [
    { name: 'Facebook', url: 'https://www.facebook.com/echopus', icon: 'facebook-01' },
    { name: 'Instagram', url: 'https://www.instagram.com/echoopus/', icon: 'instagram' },
    { name: 'LinkedIn', url: 'https://www.linkedin.com/company/echopus/', icon: 'linkedin-01' },
    { name: 'TikTok', url: 'https://www.tiktok.com/@echoopus', icon: 'tiktok' },
]

const isMenuOpen = ref(false)
const isScrolled = ref(false)

const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
}
const closeMenu = () => {
    isMenuOpen.value = false
}

// Freeze page scroll behind the open drawer.
watch(isMenuOpen, (open) => {
    if (!import.meta.client) return
    document.documentElement.style.overflow = open ? 'hidden' : ''
    if (open) $lenis?.value?.stop()
    else $lenis?.value?.start()
})

watch(() => route.fullPath, closeMenu)

const onScroll = () => {
    isScrolled.value = window.scrollY > 24
}
const onKeydown = (e) => {
    if (e.key === 'Escape') closeMenu()
}

onMounted(() => {
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('keydown', onKeydown)
    document.documentElement.style.overflow = ''
})

// Segmented EN / ع switch, shared by the bar and the drawer.
const LocaleToggle = (props, { emit }) =>
    h(
        'div',
        { class: 'locale_toggle', role: 'group', 'aria-label': t('nav.language') },
        props.locales.map((loc) =>
            h(
                'button',
                {
                    type: 'button',
                    lang: loc.language,
                    'aria-pressed': props.current === loc.code,
                    class: ['locale_option', { 'is-active': props.current === loc.code }],
                    onClick: () => emit('change', loc.code),
                },
                props.long ? loc.name : loc.code === 'ar' ? 'ع' : 'EN',
            ),
        ),
    )
LocaleToggle.props = ['locales', 'current', 'long']
LocaleToggle.emits = ['change']
</script>

<style scoped>
.dark_glass {
    background: rgba(20, 20, 28, 0.9);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-left: 1px solid rgb(var(--primary-rgb) / 0.3);
    border-right: 1px solid rgb(var(--primary-rgb) / 0.3);
    box-shadow: rgb(var(--primary-rgb) / 0.3) 0px 1px 0px 0px;
}

.header_bar {
    transition: height 0.3s ease;
}

/* nav links: brackets slide in on hover, stay on the active page */
.nav_link {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 0.75rem;
    white-space: nowrap;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.72);
    transition: color 0.2s ease;
}
.nav_bracket {
    color: var(--color-primary);
    opacity: 0;
    transform: translateX(var(--shift, 4px));
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.nav_bracket:last-child {
    --shift: -4px;
}
.nav_link:hover,
.nav_link:focus-visible,
.nav_link.is-active {
    color: #fff;
}
.nav_link:hover .nav_bracket,
.nav_link:focus-visible .nav_bracket,
.nav_link.is-active .nav_bracket {
    opacity: 1;
    transform: none;
}
.nav_link.is-active {
    color: var(--color-primary);
}

/* primary cta */
.header_cta {
    align-items: center;
    gap: 0.5rem;
    height: 42px;
    padding: 0 1.1rem;
    white-space: nowrap;
    font-family: 'Cinetype-bold', 'Cinetype-bold fallback';
    color: var(--color-dark);
    background: var(--color-primary);
    border: 1px solid var(--color-primary);
    transition: background 0.2s ease, color 0.2s ease;
}
.header_cta:hover {
    color: var(--color-primary);
    background: rgb(var(--primary-rgb) / 0.08);
}
.header_cta svg {
    transition: transform 0.2s ease;
}
.header_cta:hover svg {
    transform: translateX(3px);
}
[dir='rtl'] .header_cta:hover svg {
    transform: scaleX(-1) translateX(3px);
}

/* language switch */
:deep(.locale_toggle) {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    border: 1px solid var(--color-primary-light);
    border-radius: 999px;
}
:deep(.locale_option) {
    min-width: 2.25rem;
    height: 28px;
    padding: 0 0.7rem;
    border-radius: 999px;
    font-size: 0.75rem;
    color: rgb(var(--primary-rgb) / 0.6);
    transition: background 0.2s ease, color 0.2s ease;
}
:deep(.locale_option:hover) {
    color: var(--color-primary);
}
:deep(.locale_option.is-active) {
    color: var(--color-dark);
    background: var(--color-primary);
    font-family: 'Cinetype-bold', 'Cinetype-bold fallback';
}

/* drawer items stagger in */
.mobile_item {
    opacity: 0;
    transform: translateY(12px);
    transition: opacity 0.35s ease, transform 0.35s ease;
}
.mobile_item.is-open {
    opacity: 1;
    transform: none;
}
.mobile_link.is-active .font-pp {
    color: var(--color-primary);
}

@media (prefers-reduced-motion: reduce) {
    .header_bar,
    .mobile_item,
    .nav_bracket {
        transition: none;
    }
}
</style>
